import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  fetchCredits,
  fetchGenerators,
  fetchHealth,
  fetchIntegrations,
  getApiKey,
  listJobs,
  listProjects,
  listSpools,
  setApiKey,
  type Credits,
  type Health,
} from "../api";
import { useToast } from "../lib/toast";
import { useT } from "../i18n";
import { ACCENTS, applyAccent } from "../theme";
import { CommandPalette } from "./CommandPalette";
import { Icon } from "./Icon";

const NAV: ({ section: string } | { to: string; label: string; ico: string; metaKey?: string; end?: boolean })[] = [
  { section: "Atelier" },
  { to: "/dashboard", label: "Dashboard", ico: "dashboard" },
  { to: "/create", label: "Créer", ico: "sparkle" },
  { to: "/projects", label: "Projets", ico: "projects", metaKey: "projects" },
  { to: "/studio", label: "Studio 3D", ico: "studio" },
  { section: "Création" },
  { to: "/generators", label: "Générateurs", ico: "generators", metaKey: "generators" },
  { section: "Production" },
  { to: "/production", label: "Production", ico: "route", metaKey: "jobs" },
  { section: "Système" },
  { to: "/printers", label: "Imprimantes", ico: "printer" },
  { to: "/filaments", label: "Filaments", ico: "box", metaKey: "spools" },
  { to: "/integrations", label: "Intégrations", ico: "plug", metaKey: "integrations" },
  { section: "Compte" },
  { to: "/community", label: "Communauté", ico: "users" },
  { to: "/profile", label: "Profil", ico: "user" },
  { to: "/settings", label: "Réglages", ico: "shield" },
];


function PipelineRail() {
  const stages = [
    { tag: "STUDIO", pct: 100, color: "var(--accent)" },
    { tag: "INSPECTOR", pct: 78, color: "var(--ok)" },
    { tag: "FOUNDRY", pct: 45, color: "var(--warn)" },
  ];
  return (
    <div className="pipeline-rail">
      <div className="pl-head">
        <span className="eyebrow">Pipeline</span>
        <span className="dot" style={{ color: "var(--ok)" }} />
      </div>
      {stages.map((s) => (
        <div key={s.tag} className="pl-stage">
          <span className="pl-tag">{s.tag}</span>
          <span className="pl-bar"><i style={{ width: `${s.pct}%`, background: s.color }} /></span>
        </div>
      ))}
    </div>
  );
}

function Toaster() {
  const { message, kind } = useToast();
  if (!message) return null;
  return (
    <div className={`toast ${kind}`}>
      <Icon name={kind === "bad" ? "alert" : "check"} size={15} />
      {message}
    </div>
  );
}

const CRUMB: Record<string, string> = {
  "": "Dashboard",
  dashboard: "Dashboard",
  create: "Créer",
  projects: "Projets",
  studio: "Studio 3D",
  generators: "Générateurs",
  printers: "Imprimantes",
  filaments: "Filaments",
  integrations: "Intégrations",
  production: "Production",
  community: "Communauté",
  profile: "Profil",
  settings: "Réglages",
};

export function Layout() {
  const tr = useT();
  const [health, setHealth] = useState<Health | null>(null);
  const [credits, setCredits] = useState<Credits | null>(null);
  const [meta, setMeta] = useState<Record<string, string>>({});
  const [key, setKey] = useState(getApiKey());
  const [editKey, setEditKey] = useState(false);
  const [accent, setAccent] = useState(() => Number(localStorage.getItem("pf_accent") || 0));
  const [density, setDensity] = useState(() => localStorage.getItem("pf_density") || "regular");
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const loc = useLocation();

  // Close the mobile nav drawer whenever the route changes.
  useEffect(() => { setNavOpen(false); }, [loc.pathname]);

  // Global ⌘K / Ctrl-K opens the command palette.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => applyAccent(accent), [accent]);
  useEffect(() => {
    document.documentElement.setAttribute("data-density", density);
    localStorage.setItem("pf_density", density);
  }, [density]);

  useEffect(() => {
    fetchHealth().then(setHealth).catch(() => setHealth(null));
    fetchCredits().then(setCredits).catch(() => setCredits(null));
    Promise.allSettled([listProjects(), fetchGenerators(), fetchIntegrations(), listJobs(), listSpools()]).then(([p, g, i, j, s]) => {
      const m: Record<string, string> = {};
      if (p.status === "fulfilled") m.projects = String(p.value.length);
      if (g.status === "fulfilled") m.generators = String(g.value.length);
      if (i.status === "fulfilled") m.integrations = `${i.value.summary.configured}/${i.value.summary.total}`;
      if (j.status === "fulfilled" && j.value.length) m.jobs = String(j.value.length);
      if (s.status === "fulfilled" && s.value.length) m.spools = String(s.value.length);
      setMeta(m);
    });
  }, []);

  const seg = loc.pathname.split("/").filter(Boolean)[0] ?? "";

  // Tab title follows the current page (and re-renders on language change).
  useEffect(() => { document.title = `${tr(CRUMB[seg] ?? "Projet")} · 3DPrintForge`; });

  return (
    <div className="shell">
      {navOpen && <div className="sidebar-backdrop" onClick={() => setNavOpen(false)} />}
      <aside className={`sidebar${navOpen ? " open" : ""}`}>
        <NavLink to="/" className="brand" title="Accueil (site vitrine)" style={{ textDecoration: "none", color: "inherit" }}>
          <div className="brand-mark">◈</div>
          <div>
            <div className="brand-name">3DPrintForge</div>
            <div className="brand-sub">{tr("idée → imprimable")}</div>
          </div>
        </NavLink>

        {NAV.map((n, i) =>
          "section" in n ? (
            <div key={`s${i}`} className="section-label">{tr(n.section)}</div>
          ) : (
            <NavLink key={n.to} to={n.to} end={n.end} className="nav-link" title={tr(n.label)}>
              <Icon name={n.ico} />
              <span className="nav-label">{tr(n.label)}</span>
              {n.metaKey && meta[n.metaKey] && <span className="nav-meta">{meta[n.metaKey]}</span>}
            </NavLink>
          ),
        )}

        <PipelineRail />

        <div className="sidebar-foot">
          {credits?.configured && (
            <div className="row between" style={{ marginBottom: 9, padding: "7px 10px", borderRadius: 8, border: "1px solid var(--accent-line)", background: "var(--accent-soft)" }}>
              <span className="eyebrow" style={{ color: "var(--accent-3)" }}>{tr("Crédits")}</span>
              <span className="num" style={{ fontSize: 13, color: "var(--accent-fg)" }}>
                {credits.error ? "—" : `${credits.balance ?? "?"} ${credits.currency ?? ""}`}
              </span>
            </div>
          )}
          <div className="row" style={{ marginBottom: 9, fontSize: 11.5 }}>
            <span className="dot" style={{ color: health ? "var(--ok)" : "var(--bad)" }} />
            <span className="muted num">{health ? `API v${health.version} · ${health.env}` : tr("API hors-ligne")}</span>
          </div>
          <div className="row between" style={{ marginBottom: 9 }}>
            <div className="row" style={{ gap: 6 }}>
              {ACCENTS.map((a, idx) => (
                <button
                  key={idx}
                  title="Accent"
                  onClick={() => { setAccent(idx); localStorage.setItem("pf_accent", String(idx)); }}
                  style={{
                    width: 18, height: 18, borderRadius: 6, cursor: "pointer", padding: 0,
                    background: `linear-gradient(135deg, ${a[0]}, ${a[1]})`,
                    border: idx === accent ? "2px solid #fff" : "1px solid var(--line-2)",
                  }}
                />
              ))}
            </div>
            <button
              className="btn ghost sm"
              title={tr("Densité")}
              style={{ padding: "3px 7px" }}
              onClick={() => setDensity((d) => (d === "compact" ? "regular" : d === "regular" ? "comfy" : "compact"))}
            >
              {tr(density === "compact" ? "Compact" : density === "comfy" ? "Confort" : "Normal")}
            </button>
          </div>
          {editKey ? (
            <div className="row" style={{ gap: 6 }}>
              <input style={{ padding: "6px 8px", fontSize: 11 }} value={key} placeholder="x-api-key" onChange={(e) => setKey(e.target.value)} />
              <button className="btn sm primary" onClick={() => { setApiKey(key); setEditKey(false); }}>OK</button>
            </div>
          ) : (
            <button className="btn ghost sm" onClick={() => setEditKey(true)} style={{ width: "100%", justifyContent: "flex-start" }}>
              <Icon name="shield" size={14} /> {key ? tr("Clé API définie") : tr("Définir une clé API")}
            </button>
          )}
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <button className="burger" aria-label="Menu" onClick={() => setNavOpen(true)}><Icon name="menu" size={20} /></button>
          <div className="crumbs">3DPrintForge / <b>{tr(CRUMB[seg] ?? "Projet")}</b></div>
          <button className="topsearch" onClick={() => setPaletteOpen(true)} title="Recherche globale (⌘K)">
            <Icon name="search" size={15} />
            <span className="topsearch-ph">{tr("Rechercher projets, fichiers, générateurs…")}</span>
            <span className="kbd">⌘K</span>
          </button>
          <a className="btn ghost sm" href="/api/v1/docs" target="_blank" rel="noreferrer">
            <Icon name="external" size={14} /> API
          </a>
        </header>
        <main className="content">
          <Outlet />
        </main>
      </div>
      <Toaster />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
